import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Brain, Target } from 'lucide-react'
import { Badge, Button, Card, DataTable, EmptyState, Page, Select, TextField, type DataTableColumn } from '@/components/ui'
import { useAcdmcOptions } from '@/hooks/useAcdmc'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useAttainment,
  useCreateIntervention,
  useIntelligenceDashboard,
  type OutcomeAttainment,
} from '@/services/acdmcIntelligenceService'
import { classSelectOptions, errorMessage, sectionSelectOptions } from '@/components/acdmc/AcdmcUi'
import '@/components/acdmc/acdmc.css'

const statusTone = (s: string) => {
  if (s === 'achieved') return 'success'
  if (s === 'needs_reinforcement') return 'danger'
  if (s === 'insufficient_evidence') return 'warning'
  return 'neutral'
}

const AcdmcIntelligence: React.FC = () => {
  const { can } = usePermissions()
  const canWrite = can('acdmc.intelligence.write')
  const { data: dashboard } = useIntelligenceDashboard()
  const { data: options } = useAcdmcOptions()
  const [className, setClassName] = useState('')
  const [section, setSection] = useState('')
  const [subjectName, setSubjectName] = useState('')
  const attainment = useAttainment(className && subjectName ? { className, section, subjectName } : null)
  const createIntervention = useCreateIntervention()

  const columns: DataTableColumn<OutcomeAttainment>[] = [
    { key: 'code', header: 'Outcome', mobile: 'title', render: (r) => r.code },
    { key: 'desc', header: 'Description', wrap: true, render: (r) => r.description },
    {
      key: 'status',
      header: 'Attainment',
      render: (r) => (
        <Badge tone={statusTone(r.status)}>
          {r.attainmentPercent != null ? `${r.attainmentPercent}% · ${r.status}` : r.status}
        </Badge>
      ),
    },
    { key: 'evidence', header: 'Evidence', numeric: true, align: 'right', render: (r) => r.evidenceCount },
    {
      key: 'explain',
      header: 'Explanation',
      wrap: true,
      render: (r) => r.explanation?.[0] || '—',
    },
  ]

  const planIntervention = async (row: OutcomeAttainment) => {
    if (!canWrite) return
    try {
      await createIntervention.mutateAsync({
        title: `Reinforce ${row.code}`,
        className,
        section,
        subjectName,
        learningOutcomeId: row.learningOutcomeId,
        interventionType: 'remedial_teaching',
        baselineEvidence: { attainment: row },
        status: 'planned',
      })
      toast.success('Intervention draft created')
    } catch (e) {
      toast.error(errorMessage(e, 'Could not create intervention'))
    }
  }

  return (
    <Page
      title="Academic intelligence"
      description="Evidence-based learning outcome attainment — separate from syllabus coverage (teaching) and raw exam percentages."
    >
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Open gaps</div>
          <div className="text-lg font-semibold">{dashboard?.openGaps ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Active interventions</div>
          <div className="text-lg font-semibold">{dashboard?.activeInterventions ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Pending mappings</div>
          <div className="text-lg font-semibold">{dashboard?.pendingOutcomeMappings ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Method</div>
          <div className="text-sm font-medium">{dashboard?.methodology || 'attainment_v1'}</div>
        </Card>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-medium">
          <Brain className="h-4 w-4" />
          Learning outcome explorer
        </div>
        <div className="flex flex-wrap gap-3">
          <Select
            label="Class"
            value={className}
            onChange={(e) => {
              setClassName(e.target.value)
              setSection('')
            }}
            options={[{ value: '', label: 'Select…' }, ...classSelectOptions(options)]}
          />
          {className ? (
            <Select
              label="Section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              options={[{ value: '', label: '—' }, ...sectionSelectOptions(options, className)]}
            />
          ) : null}
          <TextField label="Subject" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} />
        </div>
        {attainment.data && (
          <>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Target className="h-4 w-4" />
              Teaching coverage (execution): {String(attainment.data.teachingCoverage?.coveragePercent ?? '—')}% weighted ·
              Attainment below uses EXMCL evidence + verified mappings.
            </p>
            {attainment.data.attainments?.length ? (
              <DataTable
                columns={[
                  ...columns,
                  ...(canWrite
                    ? [
                        {
                          key: 'act',
                          header: '',
                          render: (r: OutcomeAttainment) => (
                            <Button size="sm" variant="secondary" onClick={() => planIntervention(r)}>
                              Plan intervention
                            </Button>
                          ),
                        },
                      ]
                    : []),
                ]}
                rows={attainment.data.attainments}
                getRowKey={(r) => r.learningOutcomeId}
              />
            ) : (
              <EmptyState title="No outcomes" body="Publish curriculum adoption and map syllabus topics to platform outcomes." />
            )}
          </>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4" />
          Evidence rules
        </div>
        <ul className="mt-2 text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li>Unverified mappings are not used for authoritative attainment.</li>
          <li>Missing assessment data shows as insufficient evidence — not as failure.</li>
          <li>Student-level views require separate authorization (not exposed to parents in this phase).</li>
        </ul>
      </Card>
    </Page>
  )
}

export default AcdmcIntelligence
